// Plain-English representation of category matching patterns.
//
// The server stores anchored regexes (`invoice_regex` / `remark_regex` on `category`,
// compiled as ^...$ by CategoryMap). This module round-trips the shapes those regexes
// actually take in practice (literals and \d+ number runs, optionally wrapped in .* for
// starts-with / contains) into an editable structure; anything fancier falls back to an
// "advanced" raw-regex representation. The server remains the source of truth — this is
// purely an authoring/display convenience, and the authoritative affected-deduction set
// always comes from the preview endpoint.

export type PatternSegment = { kind: "literal"; text: string } | { kind: "numbers" }

export type Pattern =
  | { kind: "exact"; segments: PatternSegment[] }
  | { kind: "starts_with"; segments: PatternSegment[] }
  | { kind: "contains"; segments: PatternSegment[] }
  | { kind: "advanced"; regex: string }

// characters that mean something in a regex; a literal run containing any of these
// can't be represented by the simple builder
const REGEX_METACHARS = /[[\](){}.*+?|\\^$]/

/**
 * Parse a stored regex into a builder-friendly pattern. Returns an `advanced` pattern
 * whenever the regex uses anything beyond literals, `\d+` runs, and leading/trailing `.*`.
 */
export function regex_to_pattern(regex: string): Pattern {
  const advanced: Pattern = { kind: "advanced", regex }
  let body = regex
  let kind: "exact" | "starts_with" | "contains" = "exact"
  const starts = body.startsWith(".*")
  const ends = body.endsWith(".*")
  if (starts && ends && body.length >= 4) {
    kind = "contains"
    body = body.slice(2, -2)
  } else if (ends) {
    kind = "starts_with"
    body = body.slice(0, -2)
  } else if (starts) {
    // "ends with" isn't offered by the builder; keep it as advanced
    return advanced
  }

  const segments: PatternSegment[] = []
  let literal = ""
  let i = 0
  while (i < body.length) {
    if (body.startsWith("\\d+", i)) {
      if (literal) {
        segments.push({ kind: "literal", text: literal })
        literal = ""
      }
      segments.push({ kind: "numbers" })
      i += 3
      continue
    }
    const ch = body[i]
    if (REGEX_METACHARS.test(ch)) {
      return advanced
    }
    literal += ch
    i += 1
  }
  if (literal) {
    segments.push({ kind: "literal", text: literal })
  }
  if (segments.length === 0) {
    return advanced
  }
  return { kind, segments }
}

/** Serialize a builder pattern back to the stored regex form. */
export function pattern_to_regex(p: Pattern): string {
  if (p.kind === "advanced") {
    return p.regex
  }
  const body = p.segments.map((s) => (s.kind === "numbers" ? "\\d+" : s.text)).join("")
  if (p.kind === "starts_with") {
    return `${body}.*`
  }
  if (p.kind === "contains") {
    return `.*${body}.*`
  }
  return body
}

/** True when a literal segment can be represented (no regex metacharacters). */
export function is_valid_literal(text: string): boolean {
  return !REGEX_METACHARS.test(text)
}

function segments_to_english(segments: PatternSegment[]): string {
  return segments.map((s) => (s.kind === "numbers" ? "numbers" : `“${s.text}”`)).join(" then ")
}

/** Human-readable description, e.g. `starts with “INV” then numbers`. */
export function pattern_to_english(p: Pattern): string {
  switch (p.kind) {
    case "advanced":
      return `matches pattern ${p.regex}`
    case "exact":
      return `is exactly ${segments_to_english(p.segments)}`
    case "starts_with":
      return `starts with ${segments_to_english(p.segments)}`
    case "contains":
      return `contains ${segments_to_english(p.segments)}`
  }
}

/**
 * Client-side match check for live example highlighting. Mirrors the server's anchored,
 * case-sensitive invoice matching closely enough for examples; the preview endpoint is
 * authoritative. Advanced patterns are attempted as real regexes and fail closed.
 */
export function pattern_matches(p: Pattern, value: string): boolean {
  const regex = pattern_to_regex(p)
  try {
    return new RegExp(`^${regex}$`).test(value)
  } catch {
    return false
  }
}

// Inline tests
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest

  describe("regex_to_pattern / pattern_to_regex round-trip", () => {
    const corpus = [
      "INV\\d+",
      "\\d+ADTHRIVE",
      "INV\\d+-\\d+",
      "FSRWGM\\d+",
      "FS",
      "A\\d+B\\d+C\\d+",
      ".*rebates.*",
      "K\\d+.*",
    ]
    for (const regex of corpus) {
      it(`round-trips ${regex}`, () => {
        const p = regex_to_pattern(regex)
        expect(p.kind).not.toBe("advanced")
        expect(pattern_to_regex(p)).toBe(regex)
      })
    }
  })

  describe("advanced fallback", () => {
    const advanced = ["SLF[A-Z0-9]+", "\\d+SCAN([A-Z0-9]{3,})", "(SPA Week Ending.*|.*special price.*)", "-?111", "WFM[A-Z]{3}\\d{7}ISEGR", ".*suffix"]
    for (const regex of advanced) {
      it(`${regex} falls back to advanced`, () => {
        const p = regex_to_pattern(regex)
        expect(p.kind).toBe("advanced")
        // advanced round-trips verbatim
        expect(pattern_to_regex(p)).toBe(regex)
      })
    }
  })

  describe("pattern_matches", () => {
    it("matches exact literal+numbers", () => {
      const p = regex_to_pattern("INV\\d+")
      expect(pattern_matches(p, "INV123")).toBe(true)
      expect(pattern_matches(p, "INV")).toBe(false)
      expect(pattern_matches(p, "XINV123")).toBe(false)
    })
    it("matches contains", () => {
      const p = regex_to_pattern(".*rebates.*")
      expect(pattern_matches(p, "Bill - Rebates for June")).toBe(false) // case-sensitive like invoice matching
      expect(pattern_matches(p, "monthly rebates due")).toBe(true)
    })
    it("matches starts_with", () => {
      const p = regex_to_pattern("K\\d+.*")
      expect(pattern_matches(p, "K123-XYZ")).toBe(true)
      expect(pattern_matches(p, "CL123")).toBe(false)
    })
    it("fails closed on invalid advanced regex", () => {
      expect(pattern_matches({ kind: "advanced", regex: "([A-Z" }, "ABC")).toBe(false)
    })
  })

  describe("pattern_to_english", () => {
    it("describes builder patterns", () => {
      expect(pattern_to_english(regex_to_pattern("INV\\d+"))).toBe("is exactly “INV” then numbers")
      expect(pattern_to_english(regex_to_pattern(".*rebates.*"))).toBe("contains “rebates”")
      expect(pattern_to_english(regex_to_pattern("K\\d+.*"))).toBe("starts with “K” then numbers")
    })
  })
}
